
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSettings } from '@/context/SettingsContext';
import { usePerceptiveSystemStats } from '@/hooks/usePerceptiveSystemStats';
import { useCharacterClass } from '@/hooks/useCharacterClass';
import RpgPerceptiveSystemStat from './RpgPerceptiveSystemStat';
import { Crown, Sparkles, Trophy } from 'lucide-react';

const RpgCharacterSheet: React.FC = () => {
  const { darkMode } = useSettings();
  const { stats, loading } = usePerceptiveSystemStats();
  const { characterClass, totalLevel, title } = useCharacterClass(stats);

  const averageSuccessRate = stats.length > 0 
    ? Math.round(stats.reduce((sum, stat) => sum + stat.successRate, 0) / stats.length)
    : 0;
  const totalEntries = stats.reduce((sum, stat) => sum + stat.totalEntries, 0);

  const backgroundPattern = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M0 0h100v100H0z' fill='none'/%3E%3Cpath d='M0 0l100 100M100 0L0 100' stroke='%23000' stroke-width='0.2' opacity='0.1'/%3E%3C/svg%3E")`;

  if (loading) {
    return (
      <Card className={cn(
        "relative overflow-hidden",
        darkMode 
          ? "bg-gradient-to-br from-amber-900/20 via-purple-900/30 to-amber-800/20 border-amber-600/30" 
          : "bg-gradient-to-br from-amber-50 via-purple-50 to-amber-100 border-amber-300/30"
      )}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: backgroundPattern }} />
        <CardHeader className="pb-3 relative z-10">
          <CardTitle className="text-lg flex items-center gap-2 text-amber-100 font-montserrat font-bold text-shadow">
            <Crown className="h-5 w-5 text-yellow-500" />
            Character Sheet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-20 bg-amber-900/20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "relative overflow-hidden font-montserrat",
      darkMode 
        ? "bg-gradient-to-br from-amber-900/20 via-purple-900/30 to-amber-800/20 border-amber-600/30 text-amber-50" 
        : "bg-gradient-to-br from-amber-50 via-purple-50 to-amber-100 border-amber-300/30 text-amber-900"
    )}>
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-15" style={{ backgroundImage: backgroundPattern }} />
      
      <CardHeader className="pb-3 relative z-10">
        <CardTitle className="text-xl flex items-center justify-between font-bold">
          <div className="flex items-center gap-2">
            <Crown className="h-6 w-6 text-yellow-500 drop-shadow-md" />
            <span className={cn(
              "drop-shadow-md font-bold",
              darkMode ? "text-amber-100" : "text-amber-900"
            )}>
              Adventurer's Chronicle
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Badge variant="outline" className={cn(
              "border-amber-500/60 bg-amber-900/40 font-semibold text-sm px-3 py-1",
              darkMode ? "text-amber-200 border-amber-400/60" : "text-amber-800 border-amber-600/60 bg-amber-100/80"
            )}>
              <span className="mr-2 text-base">{characterClass.icon}</span>
              {characterClass.name}
            </Badge>
          </div>
        </CardTitle>
        
        <div className="flex items-center gap-6 text-base mt-3 font-medium">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-400 drop-shadow-sm" />
            <span className={cn(
              "drop-shadow-sm font-semibold",
              darkMode ? "text-purple-200" : "text-purple-700"
            )}>
              {title} (Lv.{totalLevel})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-green-400 drop-shadow-sm" />
            <span className={cn(
              "drop-shadow-sm font-semibold",
              darkMode ? "text-green-200" : "text-green-700"
            )}>
              {averageSuccessRate}% mastery
            </span>
          </div>
        </div>
        
        <p className={cn(
          "text-sm italic mt-2 font-medium drop-shadow-sm",
          darkMode ? "text-amber-300" : "text-amber-700"
        )}>
          "{characterClass.description}"
        </p>
      </CardHeader>
      
      <CardContent className="relative z-10">
        {totalEntries === 0 ? (
          <div className={cn(
            "text-center py-8 border-2 border-dashed border-amber-500/40 rounded-lg bg-black/10",
            darkMode ? "text-amber-100" : "text-amber-800"
          )}>
            <Crown className="h-16 w-16 mx-auto mb-3 text-amber-500/60" />
            <p className="text-base font-semibold mb-2">Begin your adventures to forge your legend!</p>
            <p className="text-sm font-medium italic">Complete journal quests to unlock your true potential</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className={cn(
                "text-base font-bold mb-3 drop-shadow-sm",
                darkMode ? "text-amber-200" : "text-amber-800"
              )}>
                Perceptive Arts Mastery
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.map((stat) => (
                <RpgPerceptiveSystemStat key={stat.systemId} stat={stat} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RpgCharacterSheet;
